import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import securityApi from '../api/securityApi';
import SidebarLayout from '../components/SidebarLayout';
import 'highlight.js/styles/github-dark.css';
import './BlogPostPage.css';

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBlogPost();
  }, [slug]);

  const fetchBlogPost = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await securityApi.getBlogPost(slug);
      setPost(response.post);
    } catch (err) {
      console.error('Error fetching blog post:', err);
      if (err.response?.status === 404) {
        setError('Blog post not found.');
      } else {
        setError('Failed to load blog post. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-50 text-red-700 border-red-300',
      high: 'bg-orange-50 text-orange-700 border-orange-300',
      medium: 'bg-yellow-50 text-yellow-700 border-yellow-300',
      low: 'bg-blue-50 text-blue-700 border-blue-300',
    };
    return colors[severity?.toLowerCase()] || 'bg-gray-50 text-gray-700 border-gray-300';
  };

  if (loading) {
    return (
      <SidebarLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            <p className="mt-4 text-gray-600">Loading blog post...</p>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  if (error) {
    return (
      <SidebarLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-8 max-w-md text-center">
            <h2 className="text-2xl font-bold text-red-800 mb-4">Error</h2>
            <p className="text-red-700 mb-6">{error}</p>
            <button
              onClick={() => navigate('/blog')}
              className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition"
            >
              Back to Blog
            </button>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <SidebarLayout>
      <div className="min-h-screen bg-gray-50">
        {/* Header Section */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-6 py-8">
            {/* Back Button */}
            <button
              onClick={() => navigate('/blog')}
              className="mb-6 flex items-center text-teal-600 hover:text-teal-800 transition-all font-semibold hover:gap-3 gap-2"
            >
              <svg className="w-5 h-5 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Blog
            </button>

            {/* Metadata */}
            <div className="flex items-center gap-4 mb-6">
              <span className={`px-5 py-2.5 rounded-full text-sm font-bold uppercase tracking-wider border-2 ${getSeverityColor(post.severity)} shadow-sm`}>
                {post.severity} SEVERITY
              </span>
              {post.read_time_minutes && (
                <span className="text-sm text-gray-600 flex items-center gap-2 font-medium">
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {post.read_time_minutes} min read
                </span>
              )}
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {post.title}
            </h1>

            {/* Summary */}
            <p className="text-xl text-gray-600 mb-6">
              {post.summary}
            </p>

            {/* Author & Date */}
            <div className="flex items-center gap-4 text-sm text-gray-600">
              {post.author && (
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center text-white font-semibold">
                    {post.author[0].toUpperCase()}
                  </div>
                  <span>By {post.author}</span>
                </div>
              )}
              <span>•</span>
              <span>{new Date(post.published_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}</span>
            </div>

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-3 mt-6">
                {post.tags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="px-4 py-2 bg-gradient-to-r from-teal-50 to-teal-100 text-teal-700 text-sm rounded-full hover:from-teal-100 hover:to-teal-200 transition-all font-semibold border border-teal-200"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Content Section */}
        <div className="max-w-4xl mx-auto px-6 py-12">
          <article className="prose prose-lg max-w-none">
            {/* Render HTML content - sanitized to prevent XSS attacks */}
            <div
              className="blog-content"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content_html) }}
            />
          </article>

          {/* PR Link Section */}
          {post.pr_url && (
            <div className="mt-12 p-8 bg-gradient-to-br from-teal-50 to-teal-100 border-2 border-teal-300 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold text-teal-900 mb-3 flex items-center gap-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                View the Security Fix
              </h3>
              <p className="text-teal-800 mb-5 text-lg">
                Check out the pull request that fixed this vulnerability
              </p>
              <a
                href={post.pr_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-4 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all font-bold shadow-md hover:shadow-xl transform hover:-translate-y-0.5"
              >
                View PR #{post.pr_number}
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          )}

          {/* Repository Link */}
          {post.repo_url && (
            <div className="mt-6 text-center">
              <a
                href={post.repo_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-teal-600 hover:text-teal-700 text-sm"
              >
                View Repository →
              </a>
            </div>
          )}
        </div>
      </div>
    </SidebarLayout>
  );
};

export default BlogPostPage;
